/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, useState, useEffect, ReactNode } from "react"
import PageLoader from "./PageLoader"
import {
  ChallengesService,
  ChallengeRedeemCode,
  RequestPaginator,
  Challenge
} from "../sdk"
import useError from "../hooks/useError"
import Pagination from "./Pagination"
import classNames from "classnames"
import Table from "./Table"
import Button from "./Button"
import Modal from "./Modal"
import AddRedeemCodeForm from "./AddRedeemCodeForm"
import { toast } from "react-toastify"
import EditRedeemCodeForm from "./EditRedeemCodeForm"
import Message from "./Message"
import { sortersMap, sortersValuesMap } from "../constants/sorters"
import { FiltersAndSortersType, PropertyAttributes } from "../types/sorters-filters"
import Select from "./Select"

type Props = {
  challenge?: Challenge
}

const paginatorLimit = 15

const RedeemCodesList: FC<Props> = ({ challenge }) => {
  const [loading, setLoading] = useState<boolean>(true)
  const [tableUpdating, setTableUpdating] = useState<boolean>(false)

  const [addCodeModalOpen, setAddCodeModalOpen] = useState<boolean>(false)
  const [currentCodeOpen, setCurrentCodeOpen] = useState<number | null>(null)

  const [redeemCodes, setRedeemCodes] = useState<ChallengeRedeemCode[] | null>(null)

  const [paginator, setPaginator] = useState<RequestPaginator>({ offset: 0, limit: paginatorLimit })
  const [pagination, setPagination] = useState<{ current: number, total: number} | null>(null)
  const [codesCount, setCodesCount] = useState<number | null>(null)

  const [sorters, setSorters] = useState<FiltersAndSortersType["properties"] | null>(null)
  const [, setFilters] = useState<FiltersAndSortersType["properties"] | null>(null)
  const [currentSorter, setCurrentSorter] = useState<{sorter: string | null, attributes: PropertyAttributes} | null>(null)
  const [currentSorterValue, setCurrentSorterValue] = useState<string | null>(null)

  const { handleError } = useError()

  const getRedeemCodes = async(): Promise<void> => {
    try {
      const { data } = await ChallengesService.listChallengesRedeemCodes({
        paginator,
        filters: challenge && { challengeId: challenge.id },
        sorters: currentSorter && { [currentSorter?.sorter!]: currentSorterValue || currentSorter.attributes.enum[0] } || {}
      })
      setRedeemCodes(data)
    } catch (err) {
      handleError(err)
    }
  }

  const getCodesCount = async(): Promise<void> => {
    try {
      const { count, sorters, filters } = await ChallengesService.listChallengesRedeemCodes({
        paginator: { offset: 0, limit: 0 },
        getCount: true,
        getSorters: true,
        getFilters: true
      })

      setCodesCount(count || 0)
      setFilters((filters as FiltersAndSortersType).properties)
      setSorters((sorters as FiltersAndSortersType).properties)
      setCurrentSorter({ sorter: Object.entries((sorters as FiltersAndSortersType)?.properties)[0][0], attributes: Object.entries((sorters as FiltersAndSortersType)?.properties)[0][1] })
    } catch (err) {
      handleError(err)
    }
  }

  useEffect(() => {
    void (async(): Promise<void> => {
      if (!loading) {
        setTableUpdating(true)
      }
      await getRedeemCodes()
      setTableUpdating(false)
      setLoading(false)
    })()
  }, [paginator, currentSorter, currentSorterValue])

  useEffect(() => {
    void (async(): Promise<void> => {
      await getCodesCount()
    })()
  }, [])

  useEffect(() => {
    if (codesCount && Math.ceil(codesCount / paginatorLimit)) {
      setPagination({ current: 1, total: Math.ceil(codesCount / paginatorLimit) })
    }
  }, [codesCount])

  useEffect(() => {
    if (pagination && (pagination?.current - 1) * paginatorLimit !== paginator.offset) {
      setPaginator({ offset: (pagination?.current - 1) * paginatorLimit, limit: paginatorLimit })
    }
  }, [pagination])

  return (
    <>
      {loading ?
        <PageLoader className="h-screen w-screen fixed top-0 left-0" />
        :
        redeemCodes && (codesCount || 0) > 0
          ?
          <div>
            <div className="flex mb-8">
              <h1 className="text-4xl font-bold">Redeem Codes</h1>
              <Button
                className="ml-4"
                onClick={(): void => setAddCodeModalOpen(true)}
              >
                Aggiungi
              </Button>
            </div>
            <div className="users-list__sorters-wrapper">
              {sorters &&
                <>
                  <Select
                    className="w-full md:w-[200px]"
                    label="Ordina per"
                    defaultValue={currentSorter && { label: sortersMap[currentSorter!.sorter!], value: currentSorter.attributes?.enum[0] }}
                    options={Object.keys(sorters).map(key => {
                      return {
                        label: sortersMap[key],
                        value: key
                      }
                    })}
                    onChange={(value): void => {
                      setCurrentSorter({ sorter: (value as { value: string }).value, attributes: sorters[(value as { value: string }).value] })
                    }}
                  />

                  <Select
                    className="w-full md:w-[200px]"
                    defaultValue={currentSorterValue && { label: sortersValuesMap[currentSorterValue], value: currentSorterValue }}
                    options={currentSorter?.attributes?.enum?.map(key => {
                      return {
                        label: sortersValuesMap[key],
                        value: key
                      }
                    })}
                    onChange={(value): void => {
                      setCurrentSorterValue((value as { value: string }).value)
                    }}
                  />
                </>
              }
            </div>

            <Table
              className={classNames("redeemCodes__table", tableUpdating && "opacity-50 pointer-events-none")}
              headings={["ID", "Challenge", "Codice", "ID Challenge", "Stato", "Utilizzi", ""]}
              fixedColumns={[""]}
              rows={redeemCodes.map(({
                id,
                challengeName,
                redeemCode,
                challengeId,
                enabled,
                maximumUses,
                remainingUses
              }): { id: string, children: ReactNode[] } => {
                return {
                  id: id.toString(),
                  children: [
                    <span>{id}</span>,
                    <span>{challengeName}</span>,
                    <span>{redeemCode}</span>,
                    <span>{challengeId}</span>,
                    <span>{enabled ? "Abilitato" : "Disabilitato"}</span>,
                    <span>{maximumUses - remainingUses} / {maximumUses}</span>,
                    <span
                      onClick={(): void => {
                        setCurrentCodeOpen(id)
                      }}
                      className="underline hover:cursor-pointer text-indigo-700"
                    >
                      Modifica
                    </span>
                  ]
                }
              })}
            />
            {pagination && codesCount && codesCount > paginatorLimit &&
              <Pagination
                className={classNames("mt-4", tableUpdating && "opacity-50 pointer-events-none")}
                total={pagination.total}
                current={pagination.current}
                onChange={(page): void => {
                  if (page > 0 && page <= pagination.total && page !== pagination.current) {
                    setPagination({ current: page, total: pagination.total })
                  }
                }}
              />
            }
          </div>
          :
          <Message title="Nessun elemento trovato" icon="keyboard-off">
            <Button onClick={(): void => setAddCodeModalOpen(true)}>Aggiungi</Button>
          </Message>
      }

      {addCodeModalOpen &&
        <Modal
          close={(): void => setAddCodeModalOpen(false)}
          title="Aggiungi Redeem Code"
        >
          <AddRedeemCodeForm
            onSuccess={async(): Promise<void> => {
              await getCodesCount()
              setPaginator({ offset: 0, limit: paginatorLimit })
              setPagination({ current: 1, total: Math.ceil(codesCount! / paginatorLimit) })
              await getRedeemCodes()
              toast.success("Redeem Code aggiunto correttamente")
              setAddCodeModalOpen(false)
            }}
            challenge={challenge}
          />
        </Modal>
      }
      {currentCodeOpen !== null && redeemCodes &&
        <Modal
          close={(): void => setCurrentCodeOpen(null)}
          title="Modifica redeem code"
        >
          <EditRedeemCodeForm
            redeemCode={redeemCodes?.find(({ id }) => id === currentCodeOpen)!}
            onSuccess={async(): Promise<void> => {
              await getRedeemCodes()
              toast.success("Redeem Code aggiunto correttamente")
              setCurrentCodeOpen(null)
            }}
          />
        </Modal>
      }
    </>
  )
}

export default RedeemCodesList
